/**
 * Запись ГЛОБАЛЬНОЙ области сессии — единственный путь `POST /scope/set`
 * из интерфейса (AK-895).
 *
 * Жила внутри `DashboardScopeSelector` (пилюля в шапке). Пилюля убрана —
 * переключатель «Хозяйство ▾» переехал наверх сайдбара (`FarmSwitcher`), а
 * рендерится он в двух-трёх местах сразу (рельс, мобильная шторка меню,
 * мобильная шапка). Копировать запись в каждое место нельзя: разъедутся
 * ровно в тот момент, когда поправят одну копию (тот же довод, по которому
 * из дашборда когда-то вынимали само дерево). Поэтому запись — здесь, одна.
 *
 * Что делает `setScope`, по шагам и в этом порядке:
 *   1. тот же объект, что уже выбран, — сеть не трогаем вовсе;
 *   2. `POST /scope/set` — до ответа сервера ничего на экране не меняем:
 *      если сервер откажет, человек должен видеть прежнюю область, а не
 *      думать, что переключил (урок AK-790 #670 — «молчащий» set);
 *   3. ответ пришёл — сразу подменяем `current` в общем кэше
 *      `/scope/available` (оптимистично: не ждём перезапроса, кнопка
 *      переключателя показывает новое имя в тот же кадр) и инвалидируем
 *      ключ, чтобы фоновый перезапрос сверил с сервером;
 *   4. `dispatchScopeChanged` — все экраны перестраиваются без перезагрузки.
 *
 * Ошибка `set` возвращается строкой для показа рядом с деревом; список
 * остаётся выбираемым (можно попробовать ещё раз или другой объект).
 */
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { classifyApiError } from '@/lib/apiError';
import { sharedQueryKeys } from '@/lib/http/sharedQueries';
import { dispatchScopeChanged, useScopeChanged } from '@/lib/scopeEvent';
import {
  scopeKey,
  type AvailableScopes,
  type ScopeInfo,
  type ScopeSelection,
} from './ScopeTree';

export interface ScopeSetter {
  /** Сменить область сессии. `true` — сменилась (или уже была такой). */
  setScope: (s: ScopeSelection) => Promise<boolean>;
  /** Запрос в полёте — повторный выбор игнорируется. */
  saving: boolean;
  /** Текст последней ошибки `POST /scope/set`, `null` — ошибки нет. */
  error: string | null;
  clearError: () => void;
}

function withCurrent(d: AvailableScopes, s: ScopeSelection): AvailableScopes {
  const full = d.available.find((x) => scopeKey(x) === scopeKey(s));
  return {
    ...d,
    current: full ?? ({ ...s, name: s.scope_id ?? '—' } as ScopeInfo),
  };
}

export function useScopeSetter(source = 'useScopeSetter'): ScopeSetter {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ref, а не только state: два клика подряд попадают в один кадр, и
  // `saving` из замыкания второго клика ещё `false`.
  const inFlight = useRef(false);

  const setScope = useCallback(
    async (s: ScopeSelection): Promise<boolean> => {
      if (inFlight.current) return false;
      const cached = qc.getQueryData<AvailableScopes>(sharedQueryKeys.scopeAvailable);
      if (cached && scopeKey(cached.current) === scopeKey(s)) return true;

      inFlight.current = true;
      setSaving(true);
      setError(null);
      try {
        await api.post('/api/v1/scope/set', {
          scope_type: s.scope_type,
          scope_id: s.scope_id ?? null,
        });
        qc.setQueryData<AvailableScopes>(sharedQueryKeys.scopeAvailable, (d) =>
          d ? withCurrent(d, s) : d,
        );
        // Серверный `current` сдвинулся — общий кэш протух; фоновый перезапрос
        // сверит оптимистичную подмену с тем, что записал сервер.
        void qc.invalidateQueries({ queryKey: sharedQueryKeys.scopeAvailable });
        dispatchScopeChanged({
          scope_type: s.scope_type,
          scope_id: s.scope_id ?? null,
        });
        return true;
      } catch (e) {
        // Старый current остаётся на месте — это честно: сервер его не сменил.
        setError(classifyApiError(e, source).message);
        return false;
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [qc, source],
  );

  const clearError = useCallback(() => setError(null), []);

  return { setScope, saving, error, clearError };
}

/**
 * Держать `current` общего кэша в такт с ЧУЖИМИ переключениями области
 * (например, карта зовёт `/scope/set` сама и шлёт то же событие). Объект
 * есть в списке — подменяем на месте; нет (список устарел) — перечитываем.
 */
export function useSyncScopeCurrent(): void {
  const qc = useQueryClient();
  useScopeChanged((detail) => {
    const d = qc.getQueryData<AvailableScopes>(sharedQueryKeys.scopeAvailable);
    if (!d) return;
    const known = d.available.find(
      (x) => x.scope_type === detail.scope_type && (x.scope_id ?? null) === detail.scope_id,
    );
    if (known) {
      if (scopeKey(d.current) === scopeKey(known)) return;
      qc.setQueryData<AvailableScopes>(sharedQueryKeys.scopeAvailable, (prev) =>
        prev ? withCurrent(prev, known) : prev,
      );
      return;
    }
    void qc.invalidateQueries({ queryKey: sharedQueryKeys.scopeAvailable });
  });
}
